import { GAME } from '@/config/game';
import { MAPS, type ChamberSize } from '@/config/maps';
import { SPAWNS } from '@/config/waves';
import {
  clampToUnion, framePoint, hitsGate, pushOutOfGate, rectContains,
  type Door, type Piece, type Rect,
} from './geometry';
import type { ArenaLayout } from './layout-types';
import { buildRoute } from './route';

/** Obstacle in world space (pieces resolved). */
export interface ObstacleCircle {
  readonly x: number;
  readonly y: number;
  readonly radius: number;
  /** Pits block the ship but bullets fly over them */
  readonly pit: boolean;
  /** Burn pools block nothing but burn the ship flying over them */
  readonly lava: boolean;
  readonly landmark: boolean;
  /** Piece the obstacle belongs to */
  readonly piece: number;
}

const scratch = { x: 0, y: 0 };

/**
 * Room logic (no Pixi — the look is arena-view.ts): walkable pieces, doors
 * with gates, obstacles, boundary collision and spawn queries. A classic
 * arena is ONE piece; an ADVANCE route is segments + a boss chamber whose
 * outer rect starts at the origin, so bosses keep their (0,0)..(W,H) world.
 */
export class Arena {
  readonly layout: ArenaLayout;
  readonly pieces: ReadonlyArray<Piece>;
  readonly doors: Door[];
  /** The boss's piece (chamber / the whole arena) */
  readonly fight: Piece;
  /** Fight piece size — what bosses, minions and portals call the arena */
  readonly width: number;
  readonly height: number;
  /** Every outer rect (camera bounds) */
  readonly bounds: Rect;
  readonly obstacles: ReadonlyArray<ObstacleCircle>;
  /** Per-piece gate guards (fractions of the piece's outer rect) */
  readonly turrets: ReadonlyArray<ReadonlyArray<{ x: number; y: number }>>;
  /** Spawn frame for formations/slots — the active segment, else the fight */
  frame: Piece;
  /** Where to go next on a route (objective arrow + pilots) — run/advance-director */
  readonly waypoint = { active: false, chamber: false, x: 0, y: 0 };

  /** Walkable rects: piece interiors + doorways */
  private readonly walk: Rect[];
  private readonly solids: ObstacleCircle[];

  constructor(layout: ArenaLayout, chamber: ChamberSize) {
    this.layout = layout;
    const wall = GAME.arena.wallThickness;
    let obstacleDefs;
    if (layout.route) {
      const built = buildRoute(layout.route, chamber.w, chamber.h);
      this.pieces = built.pieces;
      this.doors = built.doors;
      this.bounds = built.bounds;
      obstacleDefs = built.obstacles;
      this.turrets = built.turrets;
    } else {
      const w = layout.width ?? GAME.arena.width;
      const h = layout.height ?? GAME.arena.height;
      const outer = { x: 0, y: 0, w, h };
      const inner = { x: wall, y: wall, w: w - wall * 2, h: h - wall * 2 };
      this.pieces = [{ outer, inner, dir: 'right', index: 0, u0: 0, uSpan: 1 }];
      this.doors = [];
      this.bounds = outer;
      obstacleDefs = [layout.obstacles];
      this.turrets = [layout.turrets ?? []];
    }
    this.fight = this.pieces[this.pieces.length - 1]!;
    this.frame = this.fight;
    this.width = this.fight.outer.w;
    this.height = this.fight.outer.h;
    this.walk = [...this.pieces.map((p) => p.inner), ...this.doors.map((d) => d.rect)];

    const obstacles: ObstacleCircle[] = [];
    obstacleDefs.forEach((defs, i) => {
      const o = this.pieces[i]!.outer;
      for (const d of defs) {
        obstacles.push({
          x: o.x + d.x * o.w, y: o.y + d.y * o.h, radius: d.radius,
          pit: d.pit ?? false, lava: d.lava ?? false, landmark: d.landmark ?? false, piece: i,
        });
      }
    });
    this.obstacles = obstacles;
    this.solids = obstacles.filter((o) => !o.lava);
  }

  get isRoute(): boolean {
    return this.doors.length > 0;
  }

  /** Pushes the circle out of walls, closed gates and obstacles. @returns hit */
  clampCircle(obj: { x: number; y: number }, radius: number): boolean {
    let hit = clampToUnion(obj, radius, this.walk);
    for (const door of this.doors) {
      if (door.closed && pushOutOfGate(obj, radius, door)) hit = true;
    }
    for (const ob of this.solids) {
      const dx = obj.x - ob.x;
      const dy = obj.y - ob.y;
      const minDist = ob.radius + radius;
      const d2 = dx * dx + dy * dy;
      if (d2 < minDist * minDist && d2 > 1e-6) {
        const dist = Math.sqrt(d2);
        obj.x = ob.x + (dx / dist) * minDist;
        obj.y = ob.y + (dy / dist) * minDist;
        hit = true;
      }
    }
    return hit;
  }

  /** Is the ship over a burn pool? (burn tick — i-frames pace the damage) */
  touchesLava(x: number, y: number, radius: number): boolean {
    for (const ob of this.obstacles) {
      if (!ob.lava) continue;
      if (Math.hypot(x - ob.x, y - ob.y) < ob.radius * 0.85 + radius * 0.3) return true;
    }
    return false;
  }

  /** Bullet blockers: rocks, landmarks and closed gates (bullets fly over pits). */
  hitsObstacle(x: number, y: number, radius: number): boolean {
    for (const ob of this.solids) {
      if (ob.pit) continue;
      const reach = ob.radius * 0.8 + radius;
      const dx = x - ob.x;
      const dy = y - ob.y;
      if (dx * dx + dy * dy < reach * reach) return true;
    }
    for (const door of this.doors) {
      if (door.closed && hitsGate(door, x, y, radius)) return true;
    }
    return false;
  }

  /** Is the point inside the walkable union (± margin)? */
  contains(x: number, y: number, margin = 0): boolean {
    for (const r of this.walk) if (rectContains(r, x, y, margin)) return true;
    return false;
  }

  /** Index of the piece whose walkable rect holds the point (-1 = none). */
  pieceAt(x: number, y: number, margin = 0): number {
    for (const p of this.pieces) if (rectContains(p.inner, x, y, margin)) return p.index;
    return -1;
  }

  /**
   * A clear spot near fight-piece fractions (ship spawn, portal): off rocks,
   * pits AND pools — a blocked centre spirals out to the nearest clear spot.
   */
  safePoint(nx: number, ny: number): { x: number; y: number } {
    return this.clearPoint(nx, ny, 0, 0, 0, MAPS.spawnClear, this.fight);
  }

  /** Where the ship starts: arena centre, or the far end of segment 1. */
  spawnPoint(): { x: number; y: number } {
    if (!this.isRoute) return this.safePoint(SPAWNS.player[0], SPAWNS.player[1]);
    return this.entryPoint(0);
  }

  /** A clear point just inside a piece's entry end (route start / rescue). */
  entryPoint(index: number): { x: number; y: number } {
    const piece = this.pieces[index] ?? this.fight;
    const u = (GAME.arena.wallThickness + MAPS.route.triggerDepth * 0.5) / this.alongLen(piece);
    return this.clearPoint(u, 0.5, Infinity, Infinity, 0, MAPS.spawnClear, piece);
  }

  /** Piece length along its travel direction (outer). */
  alongLen(piece: Piece): number {
    return piece.dir === 'left' || piece.dir === 'right' ? piece.outer.w : piece.outer.h;
  }

  /** True when a circle here overlaps NO obstacle (rock, pit or pool) inside `piece`. */
  isClear(x: number, y: number, radius = 40, piece: Piece = this.frame): boolean {
    for (const ob of this.obstacles) {
      const reach = ob.radius + radius;
      if ((x - ob.x) ** 2 + (y - ob.y) ** 2 < reach * reach) return false;
    }
    return rectContains(piece.inner, x, y, -radius);
  }

  /**
   * A spawn point that is clear of obstacles AND at least `minAway` from the
   * avoid point (the player). (nx, ny) are spawn-frame fractions of `piece`
   * (u along travel, v across — plain x/y in an arena). Spirals outward
   * until both hold, never leaving the piece.
   */
  clearPoint(
    nx: number, ny: number, avoidX: number, avoidY: number,
    minAway = 220, radius = 40, piece: Piece = this.frame,
  ): { x: number; y: number } {
    framePoint(piece, nx, ny, scratch);
    return this.clearAt(scratch.x, scratch.y, avoidX, avoidY, minAway, radius, piece);
  }

  /** clearPoint() for a world-space start spot. */
  clearAt(
    sx: number, sy: number, avoidX: number, avoidY: number,
    minAway: number, radius: number, piece: Piece,
  ): { x: number; y: number } {
    const away2 = (x: number, y: number): number => (x - avoidX) ** 2 + (y - avoidY) ** 2;
    const ok = (x: number, y: number): boolean =>
      this.isClear(x, y, radius, piece) && away2(x, y) >= minAway * minAway;
    if (ok(sx, sy)) return { x: sx, y: sy };
    const S = MAPS.spawnSearch;
    for (let r = S.step; r <= S.max; r += S.step) {
      for (let a = 0; a < Math.PI * 2; a += S.angle) {
        const x = sx + Math.cos(a) * r;
        const y = sy + Math.sin(a) * r;
        if (ok(x, y)) return { x, y };
      }
    }
    // Relaxed pass: any clear spot in the piece, as far from the player as possible
    let best = { x: sx, y: sy };
    let bestD = -1;
    for (let r = S.step; r <= S.max; r += S.step) {
      for (let a = 0; a < Math.PI * 2; a += S.angle) {
        const x = sx + Math.cos(a) * r;
        const y = sy + Math.sin(a) * r;
        if (this.isClear(x, y, radius, piece) && away2(x, y) > bestD) {
          bestD = away2(x, y);
          best = { x, y };
        }
      }
    }
    if (bestD < 0) this.clampCircle(best, radius);
    return best;
  }

  /** Gate animation clocks (logic step). */
  tick(dt: number): void {
    for (const d of this.doors) d.fadeT += dt;
  }

  setDoor(index: number, closed: boolean): void {
    const door = this.doors[index];
    if (!door || door.closed === closed) return;
    door.closed = closed;
    door.fadeT = 0;
  }
}
